/**
 * PII Masking Utilities
 *
 * Pure functions for masking personally identifiable information (PII) in telemetry and logs.
 * These utilities ensure sensitive data is never exposed in console output while maintaining
 * correlation capability for debugging.
 *
 * @module piiMasking
 */

/**
 * Masks a user ID by showing only the first 8 characters followed by ellipsis.
 *
 * @param userId - The user ID to mask (typically a UUID)
 * @returns Masked user ID in format "1a2b3c4d..." or empty string if input is invalid
 *
 * @example
 * ```typescript
 * maskUserId('1a2b3c4d-5e6f-7a8b-9c0d-1e2f3a4b5c6d')
 * // Returns: '1a2b3c4d...'
 *
 * maskUserId('short')
 * // Returns: 'short...'
 *
 * maskUserId('')
 * // Returns: ''
 * ```
 */
export function maskUserId(userId: string): string {
  if (!userId || typeof userId !== 'string') {
    return '';
  }

  if (userId.length <= 8) {
    return userId.length > 0 ? `${userId}...` : '';
  }

  return `${userId.substring(0, 8)}...`;
}

/**
 * Masks an email address by showing only the first character of the local part
 * and the full domain, with asterisks replacing the rest of the local part.
 *
 * @param email - The email address to mask
 * @returns Masked email in format "u***@example.com" or '[invalid email]' if format is invalid
 *
 * @example
 * ```typescript
 * maskEmail('user@example.com')
 * // Returns: 'u***@example.com'
 *
 * maskEmail('a@test.org')
 * // Returns: 'a***@test.org'
 *
 * maskEmail('invalid-email')
 * // Returns: '[invalid email]'
 *
 * maskEmail('multiple@at@signs.com')
 * // Returns: '[invalid email]'
 * ```
 */
export function maskEmail(email: string): string {
  if (!email || typeof email !== 'string') {
    return '[invalid email]';
  }

  const atIndex = email.indexOf('@');

  // Check for valid email format: exactly one @ symbol, not at start or end
  if (atIndex <= 0 || atIndex === email.length - 1 || email.lastIndexOf('@') !== atIndex) {
    return '[invalid email]';
  }

  const localPart = email.substring(0, atIndex);
  const domain = email.substring(atIndex);

  return `${localPart.charAt(0)}***${domain}`;
}

/**
 * Masks a file path by returning only the basename (filename), stripping all directory information.
 * This prevents exposing user directory structures while maintaining correlation capability.
 *
 * Security rationale: File paths can reveal OS, username, project structure, and other sensitive
 * information about the user's system.
 *
 * @param path - The file path to mask (supports both Unix / and Windows \ separators)
 * @returns The basename only, or empty string if path is invalid
 *
 * @example
 * ```typescript
 * maskFilePath('/Users/john/Documents/data.json')
 * // Returns: 'data.json'
 *
 * maskFilePath('C:\\Users\\john\\Documents\\data.json')
 * // Returns: 'data.json'
 *
 * maskFilePath('data.json')
 * // Returns: 'data.json'
 *
 * maskFilePath('')
 * // Returns: ''
 * ```
 */
export function maskFilePath(path: string): string {
  if (!path || typeof path !== 'string') {
    return '';
  }

  // Find the last occurrence of either / or \ separator
  const lastUnixSep = path.lastIndexOf('/');
  const lastWinSep = path.lastIndexOf('\\');
  const lastSep = Math.max(lastUnixSep, lastWinSep);

  // If no separator found, the entire path is the basename
  if (lastSep === -1) {
    return path;
  }

  // Return everything after the last separator
  return path.substring(lastSep + 1);
}

/**
 * Masks a Google Drive folder ID by showing only the first 6 characters followed by ellipsis.
 * Google Drive folder IDs are typically 33-44 characters long.
 *
 * @param folderId - The Google Drive folder ID to mask
 * @returns Masked folder ID in format "1a2b3c..." or empty string if input is invalid
 *
 * @example
 * ```typescript
 * maskFolderId('1a2b3c4d5e6f7g8h9i0j1k2l3m4n5o6p7q8r9')
 * // Returns: '1a2b3c...'
 *
 * maskFolderId('short')
 * // Returns: 'short...'
 *
 * maskFolderId('')
 * // Returns: ''
 * ```
 */
export function maskFolderId(folderId: string): string {
  if (!folderId || typeof folderId !== 'string') {
    return '';
  }

  if (folderId.length <= 6) {
    return folderId.length > 0 ? `${folderId}...` : '';
  }

  return `${folderId.substring(0, 6)}...`;
}
